// ============================================
// Level Selector Component
// ============================================

import { m } from 'framer-motion';
import { levelOption, dropdown, staggerContainer, staggerItem } from '@/animations';
import type { JLPTLevel, LevelInfo } from '@/types';
import { LEVEL_COLORS, LEVEL_ON_COLORS } from '@/constants';

function levelBadgeLabel(level: JLPTLevel): string {
  switch (level) {
    case 'Everyday': return '日常';
    case 'Hiragana': return 'あ';
    case 'Katakana': return 'ア';
    default: return level;
  }
}

function triggerBadgeLabel(level: JLPTLevel): string {
  switch (level) {
    case 'Everyday': return '日';
    case 'Hiragana': return 'あ';
    case 'Katakana': return 'ア';
    default: return level.replace('N', '');
  }
}

const FOUNDATION_LEVELS: JLPTLevel[] = ['Hiragana', 'Katakana'];
const PRACTICAL_LEVELS: JLPTLevel[] = ['Everyday'];

interface LevelOptionProps {
  level: LevelInfo;
  isActive: boolean;
  onClick: () => void;
}

function LevelOption({ level, isActive, onClick }: LevelOptionProps) {
  const badgeColor = LEVEL_COLORS[level.level];
  const badgeOnColor = LEVEL_ON_COLORS[level.level];

  return (
    <m.button
      type="button"
      variants={staggerItem}
      className={`
        w-full flex items-center gap-3 p-3 rounded-lg
        transition-colors text-left
        ${isActive
          ? 'bg-green-50 border-2 border-green-500'
          : 'bg-white border-2 border-transparent hover:bg-gray-50'
        }
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
      `}
      onClick={onClick}
      role="radio"
      aria-checked={isActive}
      whileHover="hover"
      whileTap={{ scale: 0.98 }}
    >
      {/* Level Badge */}
      <m.div
        className="w-10 h-10 rounded-lg flex items-center justify-center
                   font-bold text-sm shrink-0"
        style={{ backgroundColor: badgeColor, color: badgeOnColor }}
        variants={levelOption}
        animate={isActive ? 'active' : 'inactive'}
      >
        {levelBadgeLabel(level.level)}
      </m.div>

      {/* Level Info */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-800 truncate">
          {level.displayName}
        </div>
        <div className="text-sm text-gray-500">
          {level.wordCount} words
        </div>
      </div>

      {/* Active Indicator */}
      {isActive && (
        <m.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="text-green-600 font-medium text-sm"
        >
          Active
        </m.div>
      )}
    </m.button>
  );
}

// ============================================
// Level Selector Dropdown
// ============================================

interface LevelSelectorProps {
  levels: LevelInfo[];
  currentLevel: JLPTLevel;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (level: JLPTLevel) => void;
}

export function LevelSelector({
  levels,
  currentLevel,
  isOpen,
  onToggle,
  onSelect
}: LevelSelectorProps) {
  const currentLevelInfo = levels.find(l => l.level === currentLevel);
  const badgeColor = LEVEL_COLORS[currentLevel];
  const badgeOnColor = LEVEL_ON_COLORS[currentLevel];

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white shadow-sm
                   hover:shadow-md transition-shadow
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div
          className="w-6 h-6 rounded flex items-center justify-center
                     text-xs font-bold"
          style={{ backgroundColor: badgeColor, color: badgeOnColor }}
        >
          {triggerBadgeLabel(currentLevel)}
        </div>
        <span className="font-medium text-gray-700">
          {currentLevelInfo?.displayName || currentLevel}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      <m.div
        variants={dropdown}
        initial="closed"
        animate={isOpen ? 'open' : 'closed'}
        className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl
                   border border-gray-200 overflow-hidden z-50"
        role="listbox"
        aria-label="Select level"
      >
        <div className="p-2">
          {/* Foundations (Kana) */}
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">
            Foundations
          </div>

          <m.div
            variants={staggerContainer}
            initial="hidden"
            animate="show"
            className="space-y-1"
          >
            {levels
              .filter(l => FOUNDATION_LEVELS.includes(l.level))
              .map(level => (
                <LevelOption
                  key={level.level}
                  level={level}
                  isActive={level.level === currentLevel}
                  onClick={() => {
                    onSelect(level.level);
                    onToggle();
                  }}
                />
              ))}
          </m.div>

          {/* Divider */}
          <div className="my-2 px-3">
            <div className="h-px bg-gray-200" />
            <div className="text-xs text-gray-400 text-center -mt-2">
              <span className="bg-white px-2">JLPT Levels</span>
            </div>
          </div>

          <m.div
            variants={staggerContainer}
            initial="hidden"
            animate="show"
            className="space-y-1"
          >
            {levels
              .filter(l => !FOUNDATION_LEVELS.includes(l.level) && !PRACTICAL_LEVELS.includes(l.level))
              .map(level => (
                <LevelOption
                  key={level.level}
                  level={level}
                  isActive={level.level === currentLevel}
                  onClick={() => {
                    onSelect(level.level);
                    onToggle();
                  }}
                />
              ))}
          </m.div>

          {/* Divider */}
          <div className="my-2 px-3">
            <div className="h-px bg-gray-200" />
            <div className="text-xs text-gray-400 text-center -mt-2">
              <span className="bg-white px-2">Practical</span>
            </div>
          </div>

          {/* Practical Levels */}
          {levels
            .filter(l => PRACTICAL_LEVELS.includes(l.level))
            .map(level => (
              <LevelOption
                key={level.level}
                level={level}
                isActive={level.level === currentLevel}
                onClick={() => {
                  onSelect(level.level);
                  onToggle();
                }}
              />
            ))}
        </div>
      </m.div>
    </div>
  );
}

// ============================================
// Inline Level Badge (for header)
// ============================================

interface LevelBadgeProps {
  level: JLPTLevel;
  size?: 'sm' | 'md';
}

export function LevelBadge({ level, size = 'md' }: LevelBadgeProps) {
  const badgeColor = LEVEL_COLORS[level];
  const badgeOnColor = LEVEL_ON_COLORS[level];
  const sizeClasses = size === 'sm'
    ? 'px-2 py-0.5 text-xs'
    : 'px-3 py-1 text-sm';

  return (
    <span
      className={`inline-flex items-center rounded-full font-bold ${sizeClasses}`}
      style={{ backgroundColor: badgeColor, color: badgeOnColor }}
    >
      {level === 'Everyday' ? '日常' : level}
    </span>
  );
}
